import { asDetailResponse, asPaginatedResponse, useApi, useDetailApi, usePaginatedApi } from "../../../swr/useApi";
import { adminWarrantyClaimsService, api } from "../../api_client";
import type {
  WarrantyClaimDto,
  WarrantyClaimQueryDto,
  WarrantyClaimStatsDto,
} from "../../../api";

export type WarrantyClaimListDto = WarrantyClaimDto;
export type { WarrantyClaimDto, WarrantyClaimStatsDto };

export type AdminWarrantyClaimsQuery = {
  page?: number;
  pageSize?: number;
  status?: string;
};

export type AdminResolveClaimPayload = {
  status: string;
  resolutionAction?: string;
  resolutionNote?: string;
};

// Mirrors backend ADMIN_ALLOWED_TRANSITIONS
export const ADMIN_CLAIM_TRANSITIONS: Record<string, string[]> = {
  NEW: ["MEDIATING"],
  ACKNOWLEDGED: ["MEDIATING"],
  INVESTIGATING: ["MEDIATING"],
  MEDIATING: ["RESOLVED", "REJECTED", "ESCALATED"],
  RESOLVED: [],
  REJECTED: [],
  ESCALATED: [],
};

export function useAdminWarrantyClaims(query?: AdminWarrantyClaimsQuery) {
  return usePaginatedApi<WarrantyClaimListDto, AdminWarrantyClaimsQuery>({
    key: "admin-warranty-claims",
    // The generated client takes a single query object — passing positional
    // args compiled to nothing being sent, so the list was unfilterable and
    // permanently stuck on page 1.
    serviceMethod: async (q) =>
      asPaginatedResponse<WarrantyClaimListDto>(
        adminWarrantyClaimsService.adminListWarrantyClaims(q as unknown as WarrantyClaimQueryDto),
      ),
    query,
    options: { refreshInterval: 15_000 },
  });
}

export function useAdminWarrantyClaimDetail(id: string | null) {
  return useDetailApi<WarrantyClaimDto>({
    key: id ? `admin-warranty-claim-${id}` : null,
    serviceMethod: () =>
      asDetailResponse<WarrantyClaimDto>(
        adminWarrantyClaimsService.adminGetWarrantyClaimDetail(id!),
      ),
  });
}

export function useAdminWarrantyClaimStats() {
  return useApi<WarrantyClaimStatsDto>(
    "admin-warranty-claim-stats",
    () => asDetailResponse<WarrantyClaimStatsDto>(adminWarrantyClaimsService.adminGetWarrantyClaimStats()),
    { refreshInterval: 15_000 },
  );
}

export async function resolveAdminClaim(
  id: string,
  payload: AdminResolveClaimPayload,
): Promise<WarrantyClaimDto | null> {
  try {
    const res = await api.patch<WarrantyClaimDto>(
      `/api/v1/admin/warranty-claims/${id}/resolve`,
      payload,
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function addAdminClaimMessage(
  id: string,
  content: string,
): Promise<boolean> {
  try {
    await api.post(`/api/v1/admin/warranty-claims/${id}/messages`, { content });
    return true;
  } catch {
    return false;
  }
}
